import { useMemo } from 'react';

import { Major, CareerPath } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { UnderemploymentCard } from '@backrs_ui/grow-ui/src/pages/Majors/molecules/UnderemploymentCard';

import { Box, SxProps, Theme, Typography, Chip } from '@mui/material';

import { useAgentContext } from './CareerOutcomesSection.agentContext';
import { CareerPathPills } from './CareerPathPills';
import { getHighRelevanceCareerPathsForMajors } from '../majors.utils';

const STRINGS = appStrings.modules.majors.careerOutcomes;
const MAX_JOB_TITLE_LENGTH = 40;

export function CareerOutcomesSection({
  major,
  careerPaths,
}: {
  major: Major;
  careerPaths: CareerPath[];
}) {
  const displayableJobs = major.sampleJobs
    .filter((job) => job.length <= MAX_JOB_TITLE_LENGTH)
    .slice(0, 3);

  const highRelevanceCareerPaths = useMemo(
    () => getHighRelevanceCareerPathsForMajors({ major, careerPaths }),
    [major, careerPaths],
  );

  useAgentContext(major, highRelevanceCareerPaths);

  return (
    <Box sx={contentContainerSx}>
      <UnderemploymentCard major={major} />
      <Box sx={rightSideContainerSx}>
        {/* this is where associated career paths and sample roles will go */}
        <CareerPathPills highRelevanceCareerPaths={highRelevanceCareerPaths} />

        <Box sx={sampleJobsContainerSx}>
          <Typography
            variant="text-md-semibold"
            color={({ backrs }) => backrs.colors['Colors/Text/text-brand-tertiary']}
          >
            {STRINGS.sampleRoles}
          </Typography>
          <Box sx={badgeContainerSx}>
            {displayableJobs.map((job) => (
              <Chip key={job} variant="badge" label={job} size="md" sx={chipSx} type="leading" />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

const contentContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-4xl'],
  alignItems: 'flex-start',
});

const rightSideContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-2xl'],
  flex: '1 1 280px',
  maxWidth: '280px',
});

const sampleJobsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
});

const badgeContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
});

const chipSx: SxProps<Theme> = ({ backrs, typography }) => ({
  width: 'fit-content',
  '& .MuiChip-label': {
    ...typography['text-xs-medium'],
    justifyContent: 'flex-start',
  },
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  boxShadow: backrs.shadows['shadow-xs'],
  backgroundColor: backrs.colors['Colors/Base/White'],
});
